import { useUserContext } from "@/contexts/user/useUserContext";
import { benefitsUserMTXBurnMessage, benefitsNFTMTXBurnMessage } from "@/services/benefits/benefit";
import { BenefitAllocation } from "@/types/benefit-allocation/benefit-allocation";
import { useLayoutEffect, useState, useRef, useCallback, useEffect } from "react";
import QRCode from "qrcode";
import { getError } from "@/utils/get-error/get-error";
import { signMTX } from "@/services/metatransactions/signature";

const MAX_RETRIES = 2;
const SIGNATURE_FUNCTION = {
    MAGIC: signMTX,
    OAUTH: async (_address: string, metaTransactionData: unknown): Promise<string> => {
        const result = typeof metaTransactionData === "string" ? metaTransactionData : JSON.stringify(metaTransactionData)
        return await Promise.resolve(result)
    }
}

const useQrBenefit = (benefit: BenefitAllocation) => {
    const loginProvider = import.meta.env.VITE_LOGIN_PROVIDER ?? 'MAGIC';
    const signatureFunction = SIGNATURE_FUNCTION[loginProvider as keyof typeof SIGNATURE_FUNCTION];
    const { user } = useUserContext();
    const [message, setMessage] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [qrData, setQrData] = useState<string | null>(null);
    const [qrCodeImage, setQrCodeImage] = useState('');
    const intervalCountDown = useRef<NodeJS.Timeout | null>(null);
    const [remainingTime, setRemainingTime] = useState(60);

    const resetStates = () => {
        setMessage(null);
        setError(null);
        setQrData(null);
    }

    const formatNextRetry = async (callback: () => Promise<void>) => {
        let count = 3;
        const interval = setInterval(async () => {
            setMessage(`Reintentando en ${count}...`);
            count--;
            if (count === -1) {
                clearInterval(interval);
                return await callback();
            }
        }, 1000);
    }

    const getMetaTransactionMessage = useCallback(async () => {
        const { contract_address, contract_type_id, nft_token_id, nft_collection_key, owned_amount } = benefit;
        if (!nft_token_id) {
            const body = {
                user_address: user?.main_key,
                benefit_type_id: contract_type_id,
                benefit_address: contract_address,
                amount: owned_amount > 1 ? 1 : owned_amount,
                type: "burn",
            };
            const response = await benefitsUserMTXBurnMessage({ body });
            return typeof response === "string" ? response : response.data;
        } else {
            const body = {
                token_id: nft_token_id,
                collection_key: nft_collection_key ?? '',
                benefit_type_id: contract_type_id,
                benefit_address: contract_address,
                amount: owned_amount > 1 ? 1 : owned_amount,
                type: "burn",
            };
            const response = await benefitsNFTMTXBurnMessage({ body });
            return typeof response === "string" ? response : response.data;
        }
    }, [benefit, user?.main_key]);


    const initQrData = useCallback(async (retryCount: number = 1) => {
        try {
            resetStates();
            setLoading(true);
            const metaTransactionData = await getMetaTransactionMessage();
            try {
                const signature = await signatureFunction(
                    String(user?.main_key).toLowerCase(),
                    metaTransactionData,
                    user?.magic
                );
                setQrData(signature);
            } catch (error) {
                if (error instanceof Error) {
                    setError(getError(error));
                } else {
                    setError("Unknown error");
                }
            }
            setLoading(false);
        } catch (error) {
            if (retryCount > 0 && retryCount < MAX_RETRIES) {
                clearInterval(intervalCountDown.current!);
                setRemainingTime(60);
                await formatNextRetry(async () => {
                    await initQrData(retryCount + 1);
                });
            } else {
                setMessage('');
                if (error instanceof Error) {
                    setError(getError(error));
                } else {
                    setError("Unknown error");
                }
                setLoading(false);
            }
        }
    }, [getMetaTransactionMessage, user, signatureFunction]);


    const handleIntervalCountDown = useCallback(async (callback: () => Promise<void>) => {
        if (intervalCountDown.current) {
            clearInterval(intervalCountDown.current);
        }

        setRemainingTime(60);

        intervalCountDown.current = setInterval(async () => {
            if (loading) { return; }
            setRemainingTime(prev => {
                if (prev === 1) {
                    callback();
                    return 60;
                }
                return prev - 1;
            });
        }, 1000);

        return () => {
            if (intervalCountDown.current) {
                clearInterval(intervalCountDown.current);
            }
        };
    }, [loading]);

    const handleStartAndValidate = useCallback(async () => {
        try {
            if (loading) { return; }
            if (benefit?.owned_amount <= 0) { throw new Error("No benefits available"); }
            if (!user) { throw new Error("User not found"); }

            await initQrData();
            handleIntervalCountDown(async () => {
                await initQrData();
            });
        } catch (error) {
            if (intervalCountDown.current) {
                clearInterval(intervalCountDown.current);
            }
            if (error instanceof Error) {
                setError(getError(error));
            } else {
                setError("Unknown error");
            }
        }
    }, [benefit, initQrData, handleIntervalCountDown, user, loading]);


    const firstExecution1 = useRef(true);
    useLayoutEffect(() => {
        if (firstExecution1.current) {
            firstExecution1.current = false;
            handleStartAndValidate();
        }
    }, [benefit, handleStartAndValidate]);


    useEffect(() => {
        if (qrData) {
            QRCode.toDataURL(qrData)
                .then(setQrCodeImage)
                .catch(err => setError("Error generating QR Code: " + err));
        }
    }, [qrData]);

    return {
        message,
        error,
        qrData,
        loading,
        qrCodeImage,
        remainingTime,
        resetStates,
    }
}

export default useQrBenefit;